import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Space, message, Input, Select, Spin, Popconfirm } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import Heading from "../../components/Heading";
import "./style.scss";
import { TableComponent } from "../Master/CommonComponents";
import search_icon from "../../assests/images/search-icon.svg";
import { getPremiseId } from "../../utils/worker";
import Smcs_Loader from "../../assests/images/loader-new.gif";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  STATUS_OPTIONS,
  STATUS_VALUES,
  DEFAULT_HUB_ID,
  MESSAGE_TOP_OFFSET,
  DEFAULT_PAGE_SIZE,
  LOCAL_STORAGE_KEYS,
  SOURCE_HUB_APP,
  BEARER_PREFIX,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  CONFIRM_MESSAGES,
  BUTTON_LABELS,
  POPCONFIRM_TEXTS,
  PLACEHOLDERS,
  PAGE_TITLES,
  NAVIGATION_PATHS,
  DATE_FORMATS,
  TABLE_COLUMN_KEYS,
  TABLE_COLUMN_TITLES,
  TABLE_ALIGN,
  UI_SIZES,
  ALT_TEXTS,
  ICON_COLORS,
  STATUS_COLORS,
  CSS_CLASSES,
  SHOW_TOTAL_PAGINATION,
  approveButtonStyle,
  rejectButtonStyle,
} from "./constants";
import {
  getRtoRequestList,
  getRtoRequestListClear,
  updateRtoRequestStatusAction,
  updateRtoRequestStatusClear,
} from "../../actionsMethods/rtoRequestApprovalsMethods";

dayjs.extend(customParseFormat);

const mapRtoItem = (it, index) => ({
  id: `${it?.awbNumber || "awb"}_${it?.requestedStatus || it?.requestStatus || "status"}_${it?.requestedDate || ""}_${index}`,
  awbNumber: it?.awbNumber ?? "",
  cpId: String(it?.cpId ?? ""),
  cpName: it?.cpName ?? "",
  status: String(it?.requestedStatus ?? it?.requestStatus ?? "").toLowerCase().trim(),
  rtoReason: it?.rtoReason ?? "",
  remarks: it?.remarks ?? "",
  requestedDate: it?.requestedDate ?? undefined,
});

const getUserInfo = () => {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER_INFO)) || {};
  } catch (e) {
    console.error(ERROR_MESSAGES.USER_INFO_PARSE_FAILED, e);
    return {};
  }
};

const ApprovalsRTORequest = () => {
  const dispatch = useDispatch();
  const { getRtoRequestList: listState, updateRtoRequestStatus: updateState } = useSelector(
    (state) => state.rtoRequestApprovalsReducer || {}
  );

  const [pendingAction, setPendingAction] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filters
  const [awbQuery, setAwbQuery] = useState("");
  const [cpIdQuery, setCpIdQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_VALUES.REQUESTED);

  useEffect(() => {
    message.config({ top: MESSAGE_TOP_OFFSET });
  }, []);

  const toTitleCase = (value) => {
    const str = (value ?? "").toString();
    if (!str) return "";
    return str
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b([a-z])/g, (m, p1) => p1.toUpperCase());
  };

  const loading = Boolean(listState?.fetching || updateState?.fetching);

  const fetchList = useCallback(() => {
    const bearerPid = getPremiseId();
    const hubId =
      typeof bearerPid === "string" && bearerPid.startsWith(BEARER_PREFIX)
        ? bearerPid.split(" ")[1]
        : bearerPid || DEFAULT_HUB_ID;

    dispatch(
      getRtoRequestList({
        hubId,
        cpId: (cpIdQuery || "").trim(),
        awbNumber: (awbQuery || "").trim(),
        requestStatus: statusFilter,
        page: pagination.current || 1,
        limit: pagination.pageSize || DEFAULT_PAGE_SIZE,
      })
    );
  }, [dispatch, awbQuery, cpIdQuery, statusFilter, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Derived rows from Redux store with strict active status filter and unique keys
  const rows = useMemo(() => {
    if (!listState?.result) return [];
    const json = listState.result;
    const rawData = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
    let list = rawData.map(mapRtoItem);

    if (statusFilter) {
      list = list.filter((item) => {
        const itemStatus = String(item.status || "").toLowerCase();
        const target = statusFilter.toLowerCase();
        if (target === STATUS_VALUES.REQUESTED || target === "pending") {
          return itemStatus === STATUS_VALUES.REQUESTED || itemStatus === "pending";
        }
        return itemStatus === target;
      });
    }

    return list;
  }, [listState?.result, statusFilter]);

  // Derived total for pagination
  const totalCount = useMemo(() => {
    if (statusFilter && rows.length !== (listState?.result?.pagination?.totalCount ?? rows.length)) {
      return rows.length;
    }
    return listState?.result?.pagination?.totalCount ?? rows.length;
  }, [listState?.result, statusFilter, rows.length]);

  const paginationConfig = useMemo(
    () => ({
      current: pagination.current,
      pageSize: pagination.pageSize,
      total: totalCount,
      showSizeChanger: true,
      showTotal: SHOW_TOTAL_PAGINATION,
    }),
    [pagination.current, pagination.pageSize, totalCount]
  );

  // Handle errors
  useEffect(() => {
    if (listState?.error) {
      console.error(listState.error);
      message.error(ERROR_MESSAGES.FETCH_FAILED);
    }
  }, [listState?.error]);

  // Handle update status results from Redux store
  useEffect(() => {
    if (updateState?.result) {
      if (pendingAction) {
        const { nextStatus, awbNumber } = pendingAction;
        message.success(SUCCESS_MESSAGES.STATUS_UPDATED(nextStatus, awbNumber));
      }
      dispatch(updateRtoRequestStatusClear());
      setPendingAction(null);
      fetchList();
    } else if (updateState?.error) {
      if (pendingAction) {
        const { nextStatus, awbNumber } = pendingAction;
        message.error(ERROR_MESSAGES.STATUS_UPDATE_FAILED(nextStatus, awbNumber));
      }
      dispatch(updateRtoRequestStatusClear());
      setPendingAction(null);
    }
  }, [updateState?.result, updateState?.error, dispatch, pendingAction, fetchList]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(getRtoRequestListClear());
      dispatch(updateRtoRequestStatusClear());
    };
  }, [dispatch]);

  const updateRtoRequestStatus = (awbNumber, nextStatus) => {
    const user = getUserInfo();
    const approvedBy = user?.username || user?.email || "";
    setPendingAction({ awbNumber, nextStatus });
    dispatch(
      updateRtoRequestStatusAction({
        awbNumber: String(awbNumber || ""),
        status: nextStatus,
        approvedBy,
        source: SOURCE_HUB_APP,
      })
    );
  };

  const columns = useMemo(
    () => [
      {
        title: TABLE_COLUMN_TITLES.INDEX,
        key: TABLE_COLUMN_KEYS.INDEX,
        align: TABLE_ALIGN.CENTER,
        width: 70,
        render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      },
      {
        title: TABLE_COLUMN_TITLES.AWB_NUMBER,
        dataIndex: TABLE_COLUMN_KEYS.AWB_NUMBER,
        key: TABLE_COLUMN_KEYS.AWB_NUMBER,
        align: TABLE_ALIGN.CENTER,
        render: (text) => (
          <span
            style={{ color: ICON_COLORS.LINK_BLUE, cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              window.open(NAVIGATION_PATHS.SHIPMENT_TRACKER(text), NAVIGATION_PATHS.TARGET_BLANK);
            }}
          >
            {text}
          </span>
        ),
      },
      {
        title: TABLE_COLUMN_TITLES.CP_NAME,
        dataIndex: TABLE_COLUMN_KEYS.CP_NAME,
        key: TABLE_COLUMN_KEYS.CP_NAME,
        align: TABLE_ALIGN.CENTER,
        render: (text) => <span>{text || PLACEHOLDERS.EMPTY_VALUE}</span>,
      },
      {
        title: TABLE_COLUMN_TITLES.CP_ID,
        dataIndex: TABLE_COLUMN_KEYS.CP_ID,
        key: TABLE_COLUMN_KEYS.CP_ID,
        align: TABLE_ALIGN.CENTER,
        render: (text) => <span>{text || PLACEHOLDERS.EMPTY_VALUE}</span>,
      },
      {
        title: TABLE_COLUMN_TITLES.STATUS,
        dataIndex: TABLE_COLUMN_KEYS.STATUS,
        key: TABLE_COLUMN_KEYS.STATUS,
        align: TABLE_ALIGN.CENTER,
        render: (text) => {
          const val = String(text || "").toLowerCase();
          const color =
            val === STATUS_VALUES.APPROVED
              ? STATUS_COLORS.APPROVED
              : val === STATUS_VALUES.REJECTED
                ? STATUS_COLORS.REJECTED
                : undefined;
          const weight = val === STATUS_VALUES.APPROVED || val === STATUS_VALUES.REJECTED ? 600 : 400;
          const display = toTitleCase(text || "");
          return <span style={{ color, fontWeight: weight }}>{display || PLACEHOLDERS.EMPTY_VALUE}</span>;
        },
      },
      {
        title: TABLE_COLUMN_TITLES.RTO_REASON,
        dataIndex: TABLE_COLUMN_KEYS.RTO_REASON,
        key: TABLE_COLUMN_KEYS.RTO_REASON,
        align: TABLE_ALIGN.CENTER,
        render: (text) => <span>{text || PLACEHOLDERS.EMPTY_VALUE}</span>,
      },
      {
        title: TABLE_COLUMN_TITLES.REMARKS,
        dataIndex: TABLE_COLUMN_KEYS.REMARKS,
        key: TABLE_COLUMN_KEYS.REMARKS,
        align: TABLE_ALIGN.CENTER,
        render: (text) => <span>{text || PLACEHOLDERS.EMPTY_VALUE}</span>,
      },
      {
        title: TABLE_COLUMN_TITLES.REQUESTED_DATE,
        dataIndex: TABLE_COLUMN_KEYS.REQUESTED_DATE,
        key: TABLE_COLUMN_KEYS.REQUESTED_DATE,
        align: TABLE_ALIGN.CENTER,
        render: (text) => (
          <span>
            {text
              ? dayjs(text, DATE_FORMATS.INPUT).format(DATE_FORMATS.DISPLAY)
              : PLACEHOLDERS.EMPTY_VALUE}
          </span>
        ),
      },
      {
        title: TABLE_COLUMN_TITLES.ACTION,
        key: TABLE_COLUMN_KEYS.ACTION,
        align: TABLE_ALIGN.CENTER,
        render: (_, record) => {
          const currentStatus = String(record?.status || "").toLowerCase();
          const isApproved = currentStatus === STATUS_VALUES.APPROVED;
          const isRejected = currentStatus === STATUS_VALUES.REJECTED;

          const isDisabledApprove = loading || isApproved;
          const isDisabledReject = loading || isRejected || isApproved;

          const approveStyle = approveButtonStyle(isDisabledApprove);
          const rejectStyle = rejectButtonStyle(isDisabledReject);
          return (
            <Space>
              {!isRejected && (
                <Popconfirm
                  title={<span style={{ textTransform: "initial" }}>{CONFIRM_MESSAGES.APPROVE}</span>}
                  onConfirm={() => updateRtoRequestStatus(record?.awbNumber, STATUS_VALUES.APPROVED)}
                  okText={POPCONFIRM_TEXTS.YES}
                  cancelText={POPCONFIRM_TEXTS.NO}
                  disabled={isDisabledApprove}
                  icon={<QuestionCircleOutlined style={{ color: ICON_COLORS.GREEN }} />}
                >
                  <Button
                    shape={UI_SIZES.ROUND}
                    size={UI_SIZES.SMALL}
                    style={approveStyle}
                    disabled={isDisabledApprove}
                  >
                    {BUTTON_LABELS.APPROVE}
                  </Button>
                </Popconfirm>
              )}
              {!isApproved && (
                <Popconfirm
                  title={<span style={{ textTransform: "initial" }}>{CONFIRM_MESSAGES.REJECT}</span>}
                  onConfirm={() => updateRtoRequestStatus(record?.awbNumber, STATUS_VALUES.REJECTED)}
                  okText={POPCONFIRM_TEXTS.YES}
                  cancelText={POPCONFIRM_TEXTS.NO}
                  disabled={isDisabledReject}
                  icon={<QuestionCircleOutlined style={{ color: ICON_COLORS.RED }} />}
                >
                  <Button
                    shape={UI_SIZES.ROUND}
                    size={UI_SIZES.SMALL}
                    style={rejectStyle}
                    disabled={isDisabledReject}
                  >
                    {BUTTON_LABELS.REJECT}
                  </Button>
                </Popconfirm>
              )}
            </Space>
          );
        },
      },
    ],
    [pagination.current, pagination.pageSize, loading]
  );

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const clearFilters = () => {
    setAwbQuery("");
    setCpIdQuery("");
    setStatusFilter(STATUS_VALUES.REQUESTED);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <div className={CSS_CLASSES.CONTAINER} style={{ padding: "16px" }}>
      <Heading title={PAGE_TITLES.RTO_REQUEST_APPROVALS} navigateTo={NAVIGATION_PATHS.APPROVALS_SETTINGS} />
      <Spin
        spinning={loading}
        indicator={<img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} alt={ALT_TEXTS.LOADER} />}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "nowrap", marginBottom: 12, overflowX: "auto" }}>
          <Input
            placeholder={PLACEHOLDERS.SEARCH_AWB}
            value={awbQuery}
            onChange={(e) => {
              setAwbQuery(e.target.value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            allowClear
            suffix={<img src={search_icon} alt={ALT_TEXTS.SEARCH} />}
            style={{ borderRadius: "5px", height: 40, width: 300, flex: "0 0 auto" }}
          />
          <Input
            placeholder={PLACEHOLDERS.SEARCH_CP_ID}
            value={cpIdQuery}
            onChange={(e) => {
              setCpIdQuery(e.target.value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            allowClear
            suffix={<img src={search_icon} alt={ALT_TEXTS.SEARCH} />}
            style={{ borderRadius: "5px", height: 40, width: 300, flex: "0 0 auto" }}
          />
          <Select
            size={UI_SIZES.LARGE}
            className={CSS_CLASSES.SELECT_DROPDOWN}
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            options={STATUS_OPTIONS}
            style={{ width: 200, height: 40, flex: "0 0 auto" }}
          />
          <Button
            onClick={clearFilters}
            style={{ whiteSpace: "nowrap", height: 40, borderRadius: "5px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
            disabled={loading}
          >
            {BUTTON_LABELS.CLEAR}
          </Button>
        </div>

        <TableComponent
          key={statusFilter || "all_status"}
          columns={columns}
          dataSource={rows}
          rowKey="id"
          pagination={paginationConfig}
          onChange={handleTableChange}
          scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
        />
      </Spin>
    </div>
  );
};

export default ApprovalsRTORequest;
